/**
 * TicketDivider - Dashed divider line for TicketCard
 * 
 * When used with `punch` prop, automatically registers its position
 * with the parent TicketCard to create aligned punch-holes.
 * 
 * @module butterUI/primitives/TicketCard
 * 
 * @example
 * ```tsx
 * // Standard divider (no punch holes)
 * <TicketDivider />
 * 
 * // Divider that creates punch holes
 * <TicketDivider punch />
 * 
 * // Multiple dividers, only one with punch
 * <TicketDivider />
 * <TicketDivider punch />
 * <TicketDivider />
 * ```
 */

import { useEffect, useRef } from 'react';
import { cn } from '#utils';
import { useTicketCardContext } from './context';

// ============================================================================
// TicketDivider Component
// ============================================================================

export interface TicketDividerProps {
  /** 
   * When true, registers this divider's position for punch-hole alignment.
   * Only one divider per card should have this prop.
   */
  punch?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * TicketDivider Component
 * 
 * A dashed divider line that spans the full width of the card.
 * When `punch` is true, it registers its position with the parent
 * TicketCard to create aligned punch-hole divots.
 */
export function TicketDivider({ punch = false, className }: TicketDividerProps) {
  const dividerRef = useRef<HTMLDivElement>(null);
  
  // Get context (returns null if not in a TicketCard - that's okay)
  const ticketContext = useTicketCardContext();

  // Register position when punch prop is true
  useEffect(() => {
    if (punch && ticketContext && dividerRef.current) {
      const cleanup = ticketContext.registerPunchPosition(dividerRef.current);
      return cleanup;
    }
  }, [punch, ticketContext]);

  return (
    <div
      ref={dividerRef}
      className={cn(
        'ticket-divider',
        // Vertical spacing around the divider line
        'my-5',
        className
      )}
      role="separator"
      aria-hidden="true"
    />
  );
}
